<template>
	<div class="section-nav">
		<button
			v-for="item in items"
			:key="item.key"
			class="sn-item"
			:class="{ active: active === item.key }"
			:title="item.label"
			@click="go(item.key)"
		>
			<!-- Font Awesome 图标 -->
			<font-awesome-icon class="sn-fa" :icon="item.icon" />
			<span class="sn-label">{{ item.label }}</span>
		</button>
	</div>
</template>

<script>
export default {
	name: 'SectionNav',
	props: {
		// 当前所处板块：scheme / budget / works / cases
		active: { type: String, default: 'scheme' },
	},
	data() {
		return {
			items: [
				{ key: 'scheme', label: '方案', icon: 'fa-solid fa-file-lines' },
				{ key: 'budget', label: '报价', icon: 'fa-solid fa-file-invoice-dollar' },
				{ key: 'works', label: '作品', icon: 'fa-solid fa-film' },
				{ key: 'cases', label: '案例', icon: 'fa-solid fa-briefcase' },
			],
		}
	},
	methods: {
		go(key) {
			const cur = this.$route
			const curTab = String(cur.query.tab || 'scheme')
			// 悬浮导航 = 全局板块选择器：统一回到客户列表页(projectProposal)切板块。
			// - 已在列表页且就是该板块 → 不动；
			// - 在列表页切其它板块 → 原地更新 ?tab= 预选；
			// - 在客户方案页/详情页 → 跳回列表页并带上预选板块(原地切换交给页面顶部 Tab 栏)。
			// catch 兜底：吞掉 NavigationDuplicated 等冗余导航报错
			if (cur.path === '/projectProposal' && curTab === key) return
			this.$router.push({ path: '/projectProposal', query: { tab: key } }).catch(() => {})
		},
	},
}
</script>

<style scoped>
/* 灵动缓动：前快后柔，收尾略带回弹感 */
.section-nav {
	position: fixed;
	left: 12px;
	top: 50%;
	z-index: 2147483000;
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 8px;
	background: rgba(14, 20, 28, .92);
	border: 1px solid #2C3D52;
	border-radius: 14px;
	box-shadow: 0 10px 30px rgba(0, 0, 0, .4);
	/* 始终保持垂直居中；进场动画只动透明度，不破坏 translateY(-50%) */
	transform: translateY(-50%);
	animation: sn-in .55s cubic-bezier(.22,1,.36,1) both;
	transition: background .3s ease, border-color .3s ease, box-shadow .3s ease;
}
/* 进入时淡入(位移由外层 translateX 会抵消居中,故只做透明度+轻微横向,用嵌套更稳妥;这里仅淡入) */
@keyframes sn-in {
	from { opacity: 0; }
	to   { opacity: 1; }
}
.section-nav:hover {
	border-color: #3d546e;
	box-shadow: 0 14px 40px rgba(0, 0, 0, .5);
}

.sn-item {
	display: flex;
	align-items: center;
	justify-content: center;   /* 折叠态图标水平居中 */
	gap: 8px;
	background: transparent;
	border: none;
	border-radius: 10px;
	padding: 8px;             /* 四周对称 */
	cursor: pointer;
	color: #9FB0BF;
	font-size: 13px;
	font-weight: 600;
	/* 多属性分别过渡，使用回弹缓动；不再用生硬的 all .15s */
	transition:
		color .3s cubic-bezier(.22,1,.36,1),
		background-color .3s cubic-bezier(.22,1,.36,1),
		box-shadow .3s cubic-bezier(.22,1,.36,1),
		transform .35s cubic-bezier(.34,1.56,.64,1);
}
.sn-fa svg {
	width: 18px;
	height: 18px;
	transition: transform .4s cubic-bezier(.34,1.8,.64,1);
}
/* hover 单项：背景亮起、按钮轻微右移、图标弹性放大 */
.sn-item:hover {
	background: #182230;
	color: #E9EFF5;
	transform: translateX(3px);
}
.sn-item:hover .sn-fa svg { transform: scale(1.18); }

.sn-item.active {
	background: linear-gradient(135deg, #3b82f6, #2563eb);
	color: #fff;
	box-shadow: 0 4px 14px rgba(37, 99, 235, .45);
}
/* 当前项：图标轻微脉动 + 光晕呼吸 */
.sn-item.active .sn-fa svg { animation: sn-pulse 2.4s ease-in-out infinite; }
@keyframes sn-pulse {
	0%, 100% { transform: scale(1); }
	50%      { transform: scale(1.12); }
}

/* 文字标签：宽度+透明度过渡，默认折叠隐藏 */
.sn-label {
	max-width: 0;
	opacity: 0;
	overflow: hidden;
	white-space: nowrap;
	transition:
		max-width .35s cubic-bezier(.22,1,.36,1),
		opacity .25s ease;
}
/* 折叠态：去掉图标与隐藏文字间的 gap，避免右侧空一块 */
.section-nav:not(:hover) .sn-item { gap: 0; }
/* 展开态：内容左对齐；文字逐项错开(stagger)展开 */
.section-nav:hover .sn-item { justify-content: flex-start; }
.section-nav:hover .sn-label {
	max-width: 64px;
	opacity: 1;
}
.section-nav:hover .sn-item:nth-child(1) .sn-label { transition-delay: .04s; }
.section-nav:hover .sn-item:nth-child(2) .sn-label { transition-delay: .10s; }
.section-nav:hover .sn-item:nth-child(3) .sn-label { transition-delay: .16s; }
.section-nav:hover .sn-item:nth-child(4) .sn-label { transition-delay: .22s; }

/* 手机端：改为底部固定横向 Tab 栏(图标+文字常驻)，不再遮挡内容 */
@media (max-width: 768px) {
	.section-nav {
		left: 0;
		right: 0;
		top: auto;
		bottom: 0;
		transform: none;
		z-index: 1000;
		flex-direction: row;
		justify-content: space-around;
		width: 100%;
		padding: 8px 4px 12px;
		padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
		gap: 0;
		border-radius: 0;
		border-left: none;
		border-right: none;
		border-bottom: none;
		animation: none;
	}
	.sn-item {
		flex: 1;
		flex-direction: column;
		justify-content: center;
		gap: 3px;
		padding: 6px 2px;
	}
	/* 触屏无 hover：取消桌面端的位移/放大交互 */
	.sn-item:hover { transform: none; }
	.sn-item:hover .sn-fa svg { transform: none; }
	.sn-fa svg { width: 20px; height: 20px; }
	/* 文字始终显示(覆盖桌面 hover 展开逻辑) */
	.sn-label,
	.section-nav:hover .sn-label {
		max-width: 70px;
		opacity: 1;
		transition: none;
		font-size: 11px;
	}
	.section-nav:hover .sn-item { justify-content: center; }
	/* 覆盖桌面端 :not(:hover) 的 gap:0(其特异性更高)，避免触屏 sticky hover 时 gap 在 0↔3px 切换导致底栏高度抖动 */
	.section-nav:not(:hover) .sn-item { gap: 3px; }
	/* 当前项脉动在底部栏易闪烁，关闭 */
	.sn-item.active .sn-fa svg { animation: none; }
}
</style>

<style>
@import '../assets/css/alibaba.css';
/* 手机端底部栏留出空间，避免遮挡页脚/内容(仅在挂载 SectionNav 的页面生效) */
@media (max-width: 768px) {
	body { padding-bottom: 64px; }
}
</style>